import React, { useState } from 'react';
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  User,
  Calendar,
  MessageSquare,
  Upload,
  Settings,
  Star,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { 
  useTimeline, 
  useAddTimelineEntry, 
  useUpdateTimelineEntry, 
  useDeleteTimelineEntry 
} from '@/hooks/useBookings';
import type { TimelineEntry } from '@/lib/api/bookingService';

// TimelineEntry interface now imported from bookingService

interface BookingTimelineProps {
  bookingId: number;
  userRole: 'customer' | 'provider' | 'admin';
  canAddEntries?: boolean;
  canEditEntries?: boolean;
  showAddButton?: boolean;
  className?: string;
  compact?: boolean;
}

const BookingTimeline: React.FC<BookingTimelineProps> = ({
  bookingId,
  userRole,
  canAddEntries = false,
  canEditEntries = false,
  showAddButton = false,
  className = '',
  compact = false
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);
  
  // Use hooks for timeline data and mutations
  const { 
    data: timelineData, 
    isLoading: loading, 
    error: timelineError,
    refetch: refetchTimeline
  } = useTimeline(bookingId, currentPage, 20);
  
  const addTimelineEntryMutation = useAddTimelineEntry();
  const updateTimelineEntryMutation = useUpdateTimelineEntry();
  const deleteTimelineEntryMutation = useDeleteTimelineEntry();
  
  // Extract timeline and pagination from response
  const timeline = timelineData?.timeline || [];
  const pagination = timelineData?.pagination || {
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  };

  // Form state for adding/editing entries
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    milestone_type: 'progress_update' as const,
    progress_percentage: '',
    priority: 'medium' as const,
    notes: '',
    is_visible_to_customer: true,
    is_visible_to_provider: true
  });

  // Handle timeline error states
  React.useEffect(() => {
    if (timelineError && !timelineError.message?.includes('404')) {
      setError('Failed to load timeline');
    } else {
      setError(null);
    }
  }, [timelineError]);

  const handleAddEntry = async () => {
    try {
      const payload = {
        ...formData,
        progress_percentage: formData.progress_percentage ? parseFloat(formData.progress_percentage) : undefined
      };

      await addTimelineEntryMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        entry: payload
      });
      
      setShowAddForm(false);
      resetForm();
      setError(null);
      setValidationErrors(null);
    } catch (err: any) {
      console.error('Add timeline entry error:', err);
      
      // Handle validation errors specifically
      if (err.validationErrors) {
        setValidationErrors(err.validationErrors);
        setError('Please fix the validation errors below');
      } else {
        setError(err.message || 'Failed to add timeline entry');
        setValidationErrors(null);
      }
    }
  };

  const handleEditEntry = async (timelineId: number) => {
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        notes: formData.notes,
        progress_percentage: formData.progress_percentage ? parseFloat(formData.progress_percentage) : undefined,
        priority: formData.priority
      };

      await updateTimelineEntryMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        timelineId,
        entry: payload
      });
      
      setEditingEntry(null);
      resetForm();
      setError(null);
      setValidationErrors(null);
    } catch (err: any) {
      console.error('Update timeline entry error:', err);
      
      // Handle validation errors specifically
      if (err.validationErrors) {
        setValidationErrors(err.validationErrors);
        setError('Please fix the validation errors below');
      } else {
        setError(err.message || 'Failed to update timeline entry');
        setValidationErrors(null);
      }
    }
  };

  const handleDeleteEntry = async (timelineId: number) => {
    if (!confirm('Are you sure you want to delete this timeline entry?')) return;

    try {
      await deleteTimelineEntryMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        timelineId
      });
      
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete timeline entry');
      console.error('Delete timeline entry error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      milestone_type: 'progress_update',
      progress_percentage: '',
      priority: 'medium',
      notes: '',
      is_visible_to_customer: true,
      is_visible_to_provider: true
    });
    setError(null);
    setValidationErrors(null);
  };

  // Helper function to get validation error for a field
  const getFieldError = (fieldName: string): string | null => {
    return validationErrors?.[fieldName] || null;
  };

  const startEditing = (entry: TimelineEntry) => {
    setFormData({
      title: entry.title,
      description: entry.description,
      milestone_type: entry.milestone_type,
      progress_percentage: entry.progress_percentage?.toString() || '',
      priority: entry.priority,
      notes: entry.notes || '',
      is_visible_to_customer: entry.is_visible_to_customer,
      is_visible_to_provider: entry.is_visible_to_provider
    });
    setEditingEntry(entry.timeline_id);
    setShowAddForm(true);
  };

  const getMilestoneIcon = (type: string) => {
    const iconClass = compact ? 'h-4 w-4' : 'h-5 w-5';
    
    switch (type) {
      case 'status_change':
        return <Settings className={iconClass} />;
      case 'progress_update':
        return <TrendingUp className={iconClass} />;
      case 'follow_up':
        return <Calendar className={iconClass} />;
      case 'milestone':
        return <Star className={iconClass} />;
      case 'media_upload':
        return <Upload className={iconClass} />;
      case 'system_update':
        return <Settings className={iconClass} />;
      default:
        return <Clock className={iconClass} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'customer':
        return 'text-green-600 bg-green-50';
      case 'provider':
        return 'text-blue-600 bg-blue-50';
      case 'admin':
        return 'text-purple-600 bg-purple-50';
      case 'system':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse space-y-4 ${className}`}>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex space-x-4">
            <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => refetchTimeline()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className={`font-semibold text-gray-900 ${compact ? 'text-lg' : 'text-xl'}`}>
          Timeline & Progress
        </h3>
        {showAddButton && canAddEntries && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`inline-flex items-center border border-transparent font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              compact ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'
            }`}
          >
            <Plus className={`mr-2 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
            Add Update
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            {editingEntry ? 'Edit Timeline Entry' : 'Add Timeline Entry'}
          </h4>
          
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                  {validationErrors && Object.keys(validationErrors).length > 0 && (
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                      {Object.entries(validationErrors).map(([field, message]) => (
                        <li key={field}>{field}: {message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  getFieldError('title') 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter title..."
              />
              {getFieldError('title') && (
                <p className="mt-1 text-sm text-red-600">{getFieldError('title')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.milestone_type}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  milestone_type: e.target.value as any 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="progress_update">Progress Update</option>
                <option value="milestone">Milestone</option>
                <option value="follow_up">Follow-up</option>
                <option value="status_change">Status Change</option>
                <option value="media_upload">Media Upload</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  priority: e.target.value as any 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Progress %
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.progress_percentage}
                onChange={(e) => setFormData(prev => ({ ...prev, progress_percentage: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0-100"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter description..."
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes..."
            />
          </div>

          {(userRole === 'admin' || userRole === 'provider') && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility Settings
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_visible_to_customer}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_visible_to_customer: e.target.checked 
                    }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Visible to Customer</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_visible_to_provider}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_visible_to_provider: e.target.checked 
                    }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Visible to Provider</span>
                </label>
              </div>
              {(getFieldError('is_visible_to_customer') || getFieldError('is_visible_to_provider')) && (
                <div className="mt-2 text-sm text-red-600">
                  {getFieldError('is_visible_to_customer') && (
                    <p>Customer visibility: {getFieldError('is_visible_to_customer')}</p>
                  )}
                  {getFieldError('is_visible_to_provider') && (
                    <p>Provider visibility: {getFieldError('is_visible_to_provider')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex space-x-3">
            <button
              onClick={editingEntry ? () => handleEditEntry(editingEntry) : handleAddEntry}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {editingEntry ? 'Update Entry' : 'Add Entry'}
            </button>
            
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingEntry(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-4">
        {timeline.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No timeline entries yet</p>
            <p className="text-sm text-gray-400">Timeline entries will appear here as the booking progresses</p>
          </div>
        ) : (
          timeline.map((entry, index) => (
            <div
              key={entry.timeline_id}
              className={`relative flex space-x-4 ${
                index !== timeline.length - 1 ? 'pb-4' : ''
              }`}
            >
              {/* Timeline line */}
              {index !== timeline.length - 1 && (
                <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200" />
              )}

              {/* Icon */}
              <div className={`flex-shrink-0 flex items-center justify-center rounded-full border-2 ${
                getPriorityColor(entry.priority)
              } ${compact ? 'h-8 w-8' : 'h-10 w-10'}`}>
                {getMilestoneIcon(entry.milestone_type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className={`font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
                        {entry.title}
                      </h4>
                      
                      {entry.progress_percentage !== null && entry.progress_percentage !== undefined && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {entry.progress_percentage}%
                        </span>
                      )}

                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        getRoleColor(entry.created_by_role)
                      }`}>
                        {entry.created_by_role}
                      </span>
                    </div>

                    <p className={`text-gray-600 mb-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {entry.description}
                    </p>

                    {entry.notes && (
                      <div className="mt-2 p-2 bg-gray-50 rounded border-l-4 border-blue-200">
                        <p className={`text-gray-700 ${compact ? 'text-xs' : 'text-sm'}`}>
                          <MessageSquare className="inline h-4 w-4 mr-1" />
                          {entry.notes}
                        </p>
                      </div>
                    )}

                    <div className={`flex items-center space-x-4 mt-2 ${
                      compact ? 'text-xs' : 'text-sm'
                    } text-gray-500`}>
                      <span className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {entry.created_by_name || `User ${entry.created_by}`}
                      </span>
                      
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </span>

                      {!compact && (
                        <span title={format(new Date(entry.created_at), 'PPpp')}>
                          {format(new Date(entry.created_at), 'MMM dd, HH:mm')}
                        </span>
                      )}
                    </div>

                    {/* Visibility indicators */}
                    {userRole === 'admin' && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`inline-flex items-center text-xs ${
                          entry.is_visible_to_customer ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {entry.is_visible_to_customer ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          Customer
                        </span>
                        
                        <span className={`inline-flex items-center text-xs ${
                          entry.is_visible_to_provider ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {entry.is_visible_to_provider ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          Provider
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {canEditEntries && (userRole === 'admin' || entry.created_by_role === userRole) && (
                    <div className="flex space-x-1 ml-4">
                      <button
                        onClick={() => startEditing(entry)}
                        className="p-1 text-gray-400 hover:text-blue-600 rounded"
                        title="Edit entry"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteEntry(entry.timeline_id)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded"
                        title="Delete entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(prev => prev - 1)}
            disabled={pagination.current_page === 1}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600">
            Page {pagination.current_page} of {pagination.last_page}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingTimeline;